/**
 * Request List Component
 *
 * Reusable component for displaying expenditure requests in a table with:
 * - Sortable columns
 * - Filtering (status, date range, amount)
 * - Search with debouncing
 * - Pagination or Infinite Scroll
 * - Column customization with persistence
 */

import { renderIcon, type IconName } from '../utils/icons';
import { apiClient } from '../services/api-client';
import { createSkeleton } from './skeleton';
import {
  createInfiniteScroll,
  createScrollToTopButton,
  type InfiniteScrollResult,
  type ScrollToTopResult,
} from '../utils/infinite-scroll';
import { createInlineApprovalActions } from './inline-approval-actions';
import { bulkSelectionStore } from '../services/bulk-selection-store';
import { BulkActionToolbar, type BulkActionConfig } from './bulk-action-toolbar';
import { createRequestCard } from './request-card';
import { watchScreenSize, isMobile, type ScreenSize } from '../utils/responsive';
import { ColumnPicker, type ColumnDefinition } from './column-picker';
import { preferencesStore } from '../services/preferences-store';

/**
 * Request status types
 */
export type RequestStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

/**
 * Sort direction
 */
export type SortOrder = 'asc' | 'desc';

/**
 * Sort field options
 */
export type SortBy = 'created_at' | 'updated_at' | 'submitted_at' | 'amount' | 'title' | 'status' | 'category' | 'currency' | 'creator';

/**
 * Expenditure request from API
 */
export interface ExpenditureRequest {
  id: string;
  title: string | null;
  description: string | null;
  amount: number | null;
  currency: string | null;
  category?: string | null;
  status: RequestStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  // Extended fields from joins
  creator_name?: string | null;
  approver_role?: string;
  approvalProgress?: {
    total: number;
    steps: Array<{ order: number; status: 'PENDING' | 'APPROVED' | 'REJECTED' }>;
  };
}

/**
 * Pending approval with request details
 */
export interface PendingApproval {
  approvalStep: {
    id: string;
    stepOrder: number;
    approverRole: string;
    approverId: string | null;
    delegateId: string | null;
    status: string;
    createdAt: string;
  };
  request: {
    id: string;
    title: string | null;
    amount: number | null;
    currency: string | null;
    status: string;
    createdBy: string;
    createdAt: string;
    creatorName: string | null;
    creatorEmail: string | null;
  };
  isActingAsDeputy: boolean;
  onBehalfOfId: string | null;
}

/**
 * API response with pagination
 */
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Expense type (category) options for filtering
 */
export type ExpenseCategory =
  | 'TRAVEL'
  | 'EQUIPMENT'
  | 'SOFTWARE'
  | 'TRAINING'
  | 'MARKETING'
  | 'OFFICE_SUPPLIES'
  | 'PROFESSIONAL_SERVICES'
  | 'OTHER';

/**
 * Category labels for display
 */
const CATEGORY_OPTIONS: { value: ExpenseCategory; label: string }[] = [
  { value: 'OFFICE_SUPPLIES', label: 'Office Material' },
  { value: 'TRAINING', label: 'External Event/Conference' },
  { value: 'OTHER', label: 'Other' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'EQUIPMENT', label: 'Out-of-Pocket Expense' },
  { value: 'SOFTWARE', label: 'Software' },
  { value: 'PROFESSIONAL_SERVICES', label: 'Professional Services' },
];

/**
 * Filter options for the request list
 */
export interface RequestListFilters {
  status?: RequestStatus;
  category?: ExpenseCategory;
  minAmount?: number;
  maxAmount?: number;
  currency?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
  /** Filter to show only the current user's requests (useful for admins who want to see only their own) */
  mine?: boolean;
}

/**
 * Pagination mode for the request list
 */
export type PaginationMode = 'pagination' | 'infinite-scroll';

/**
 * Configuration for the request list component
 */
export interface RequestListConfig {
  /** Parent element to render into */
  parent: HTMLElement;
  /** Title to display */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Base API endpoint for fetching requests */
  endpoint: string;
  /** Show filter controls */
  showFilters?: boolean;
  /** Show search input */
  showSearch?: boolean;
  /** Show status column */
  showStatusColumn?: boolean;
  /** Show created by column */
  showCreatorColumn?: boolean;
  /** Custom empty state message */
  emptyMessage?: string;
  /** Initial filters to apply */
  initialFilters?: RequestListFilters;
  /** Callback when a request is clicked */
  onRequestClick?: (request: ExpenditureRequest) => void;
  /** Callback for custom action rendering */
  renderActions?: (request: ExpenditureRequest) => HTMLElement | null;
  /** Whether this is a pending approvals view */
  isPendingApprovals?: boolean;
  /** Pagination mode: 'pagination' (default) or 'infinite-scroll' */
  paginationMode?: PaginationMode;
  /** Show scroll-to-top button when using infinite scroll (default: true) */
  showScrollToTop?: boolean;
  /** Enable bulk selection with checkboxes */
  enableBulkSelection?: boolean;
  /** Show bulk action toolbar when items are selected */
  showBulkActions?: boolean;
  /** Callback for bulk approve action */
  onBulkApprove?: (ids: string[]) => Promise<void>;
  /** Callback for bulk reject action */
  onBulkReject?: (ids: string[]) => Promise<void>;
  /** Callback for bulk export action */
  onBulkExport?: (ids: string[]) => void;
  /** View identifier for column preferences */
  viewId?: 'my-requests' | 'my-approvals' | 'all-requests' | 'travel-requests' | 'out-of-pocket';
  /** Show column picker for customization */
  showColumnPicker?: boolean;
  /** Hide the category filter dropdown (useful for pre-filtered category views) */
  hideCategoryFilter?: boolean;
  /** Columns to hide by default (user can still enable via column picker) */
  hiddenColumns?: string[];
}

/**
 * Status configuration for display
 */
const STATUS_CONFIG: Record<RequestStatus, { label: string; icon: IconName; colorClass: string; bgClass: string }> = {
  DRAFT: {
    label: 'Draft',
    icon: 'edit',
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
  },
  PENDING: {
    label: 'Pending',
    icon: 'clock',
    colorClass: 'text-warning',
    bgClass: 'bg-warning/20',
  },
  APPROVED: {
    label: 'Approved',
    icon: 'checkCircle',
    colorClass: 'text-success',
    bgClass: 'bg-success/20',
  },
  REJECTED: {
    label: 'Rejected',
    icon: 'xCircle',
    colorClass: 'text-destructive',
    bgClass: 'bg-destructive/20',
  },
  CANCELLED: {
    label: 'Cancelled',
    icon: 'x',
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted',
  },
};

/**
 * Currency options
 */
const CURRENCIES = ['CHF', 'EUR', 'USD'];

/**
 * Format currency amount
 */
function formatAmount(amount: number | null, currency: string | null): string {
  if (amount === null) return '-';
  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: currency || 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date for display
 */
function formatDate(timestamp: string | null): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-CH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string | null | undefined): string {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Debounce function
 */
function debounce<T extends (...args: Parameters<T>) => void>(fn: T, delay: number): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Request List Component class
 */
export class RequestList {
  private container: HTMLElement;
  private config: RequestListConfig;
  private filters: RequestListFilters = {};
  private sortBy: SortBy = 'created_at';
  private sortOrder: SortOrder = 'desc';
  private page = 1;
  private limit = 20;
  private total = 0;
  private totalPages = 0;
  private requests: ExpenditureRequest[] = [];
  private allRequests: ExpenditureRequest[] = []; // Accumulated requests for infinite scroll
  private pendingApprovals: PendingApproval[] = [];
  private isLoading = false;
  private isLoadingMore = false; // For infinite scroll loading more
  private tableBody: HTMLElement | null = null;
  private cardsContainer: HTMLElement | null = null;
  private paginationContainer: HTMLElement | null = null;
  private searchInput: HTMLInputElement | null = null;
  private infiniteScroll: InfiniteScrollResult | null = null;
  private scrollToTop: ScrollToTopResult | null = null;
  private tableContainer: HTMLElement | null = null;
  private bulkActionToolbar: BulkActionToolbar | null = null;
  private selectionUnsubscribe?: () => void;
  private viewMode: 'table' | 'cards' = 'table';
  private screenSizeUnsubscribe?: () => void;
  private columnPicker: ColumnPicker | null = null;
  private visibleColumns: string[] = [];
  private columnDefinitions: ColumnDefinition[] = [];
  private table: HTMLElement | null = null;
  private thead: HTMLElement | null = null;

  constructor(config: RequestListConfig) {
    this.config = {
      showFilters: true,
      showSearch: true,
      showStatusColumn: true,
      showCreatorColumn: false,
      emptyMessage: 'No requests found',
      paginationMode: 'infinite-scroll', // Default to infinite scroll
      showScrollToTop: true,
      showColumnPicker: false,
      ...config,
    };

    if (config.initialFilters) {
      this.filters = { ...config.initialFilters };
    }

    this.container = document.createElement('div');
    this.container.className = 'request-list space-y-4';

    // Set initial view mode based on screen size
    this.viewMode = isMobile() ? 'cards' : 'table';

    // Watch for screen size changes
    this.screenSizeUnsubscribe = watchScreenSize((size: ScreenSize) => {
      const shouldUseCards = size === 'mobile';
      const newViewMode = shouldUseCards ? 'cards' : 'table';

      if (newViewMode !== this.viewMode) {
        this.viewMode = newViewMode;
        this.renderContent();
      }
    });

    // Initialize column definitions based on view
    this.initializeColumns();

    this.render();
    config.parent.appendChild(this.container);
    this.loadRequests();
  }

  /**
   * Initialize column definitions based on the view type
   */
  private initializeColumns(): void {
    // Base columns available in all views
    const baseColumns: ColumnDefinition[] = [
      { id: 'title', label: 'Title', required: true, default: true },
      { id: 'amount', label: 'Amount', required: false, default: true },
      { id: 'approval_progress', label: 'Approval', required: false, default: true },
      { id: 'category', label: 'Category', required: false, default: true },
      { id: 'currency', label: 'Currency', required: false, default: false },
      { id: 'description', label: 'Description', required: false, default: false },
      { id: 'submitted_at', label: 'Submitted Date', required: false, default: false },
      { id: 'created_at', label: 'Created Date', required: false, default: true },
      { id: 'updated_at', label: 'Updated Date', required: false, default: false },
    ];

    // Add view-specific columns
    if (this.config.showStatusColumn) {
      baseColumns.push({ id: 'status', label: 'Status', required: false, default: true });
    }

    if (this.config.showCreatorColumn) {
      baseColumns.push({ id: 'creator', label: 'Created By', required: false, default: true });
    }

    if (this.config.isPendingApprovals) {
      baseColumns.push({ id: 'approver_role', label: 'Approving As', required: false, default: true });
      baseColumns.push({ id: 'actions', label: 'Actions', required: true, default: true });
    }

    this.columnDefinitions = baseColumns;

    // Load saved preferences or use defaults
    if (this.config.viewId && this.config.showColumnPicker) {
      const savedPrefs = preferencesStore.getColumnPreferences(this.config.viewId);
      if (savedPrefs?.visibleColumns) {
        this.visibleColumns = savedPrefs.visibleColumns;
      } else {
        // Get default columns, excluding any in hiddenColumns
        const hiddenColumns = this.config.hiddenColumns || [];
        this.visibleColumns = baseColumns
          .filter(col => col.required || (col.default === true && !hiddenColumns.includes(col.id)))
          .map(col => col.id);
      }
    } else {
      // If no column picker, show all default columns (excluding hiddenColumns)
      const hiddenColumns = this.config.hiddenColumns || [];
      this.visibleColumns = baseColumns
        .filter(col => col.required || (col.default === true && !hiddenColumns.includes(col.id)))
        .map(col => col.id);
    }
  }

  /**
   * Render the component
   */
  private render(): void {
    // Cleanup previous infinite scroll and scroll-to-top if they exist
    this.cleanupInfiniteScroll();

    this.container.innerHTML = '';

    // Header section
    const header = this.createHeader();
    this.container.appendChild(header);

    // Filter bar
    if (this.config.showFilters || this.config.showSearch) {
      const filterBar = this.createFilterBar();
      this.container.appendChild(filterBar);
    }

    // Content container (table or cards based on view mode)
    this.renderContent();

    // Pagination or Infinite Scroll
    if (this.config.paginationMode === 'pagination') {
      // Traditional pagination
      this.paginationContainer = document.createElement('div');
      this.paginationContainer.className = 'flex items-center justify-between px-4 py-3 glass-sm rounded-lg';
      this.container.appendChild(this.paginationContainer);
    } else {
      // Infinite scroll
      this.setupInfiniteScroll();
    }

    // Initialize bulk selection if enabled
    if (this.config.enableBulkSelection && this.config.showBulkActions) {
      this.setupBulkActions();
    }
  }

  /**
   * Render content based on view mode (table or cards)
   */
  private renderContent(): void {
    // Remove existing content container
    if (this.tableContainer) {
      this.tableContainer.remove();
      this.tableContainer = null;
    }
    if (this.cardsContainer) {
      this.cardsContainer.remove();
      this.cardsContainer = null;
    }

    // Create new content container based on view mode
    if (this.viewMode === 'cards') {
      this.cardsContainer = this.createCardsContainer();
      // Insert after filter bar or header
      const filterBar = this.container.querySelector('.filter-bar');
      if (filterBar) {
        filterBar.after(this.cardsContainer);
      } else {
        const header = this.container.firstElementChild;
        if (header) {
          header.after(this.cardsContainer);
        } else {
          this.container.appendChild(this.cardsContainer);
        }
      }
    } else {
      this.tableContainer = this.createTableContainer();
      // Insert after filter bar or header
      const filterBar = this.container.querySelector('.filter-bar');
      if (filterBar) {
        filterBar.after(this.tableContainer);
      } else {
        const header = this.container.firstElementChild;
        if (header) {
          header.after(this.tableContainer);
        } else {
          this.container.appendChild(this.tableContainer);
        }
      }
    }

    // Re-render the data if we have it
    if (this.requests.length > 0 || this.allRequests.length > 0) {
      if (this.config.paginationMode === 'infinite-scroll') {
        this.renderContentInfinite();
      } else {
        this.renderContentPaginated();
      }
    }
  }

  /**
   * Create cards container
   */
  private createCardsContainer(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'cards-container';
    return container;
  }

  /**
   * Render content for paginated mode
   */
  private renderContentPaginated(): void {
    if (this.viewMode === 'cards') {
      this.renderCards();
    } else {
      this.renderTableRows();
    }
  }

  /**
   * Render content for infinite scroll mode
   */
  private renderContentInfinite(): void {
    if (this.viewMode === 'cards') {
      this.renderCardsInfinite();
    } else {
      this.renderTableRowsInfinite();
    }
  }

  /**
   * Render cards for paginated mode
   */
  private renderCards(): void {
    if (!this.cardsContainer) return;
    this.cardsContainer.innerHTML = '';

    if (this.requests.length === 0) {
      this.cardsContainer.innerHTML = `
        <div class="flex flex-col items-center gap-3 py-12">
          ${renderIcon('fileText', { size: 'xl', className: 'text-muted-foreground/50' }).outerHTML}
          <p class="text-muted-foreground">${escapeHtml(this.config.emptyMessage || 'No requests found')}</p>
        </div>
      `;
      return;
    }

    this.requests.forEach(request => {
      const card = createRequestCard({
        request,
        showStatus: this.config.showStatusColumn ?? true,
        showCreator: this.config.showCreatorColumn ?? true,
        showActions: this.config.isPendingApprovals ?? false,
        isPendingApproval: this.config.isPendingApprovals ?? false,
        enableBulkSelection: this.config.enableBulkSelection ?? false,
        ...(this.config.onRequestClick && { onRequestClick: this.config.onRequestClick }),
        onActionComplete: () => this.loadRequests(),
      });
      this.cardsContainer?.appendChild(card);

      // Setup bulk selection for card if enabled
      if (this.config.enableBulkSelection) {
        const checkbox = card.querySelector('.row-checkbox') as HTMLInputElement;
        if (checkbox) {
          checkbox.checked = bulkSelectionStore.isSelected(request.id);
          checkbox.addEventListener('change', (e) => {
            e.stopPropagation();
            bulkSelectionStore.toggleSelection(request.id);
          });
        }
      }
    });
  }

  /**
   * Render cards for infinite scroll mode
   */
  private renderCardsInfinite(): void {
    if (!this.cardsContainer) return;
    this.cardsContainer.innerHTML = '';

    if (this.allRequests.length === 0) {
      this.cardsContainer.innerHTML = `
        <div class="flex flex-col items-center gap-3 py-12">
          ${renderIcon('fileText', { size: 'xl', className: 'text-muted-foreground/50' }).outerHTML}
          <p class="text-muted-foreground">${escapeHtml(this.config.emptyMessage || 'No requests found')}</p>
        </div>
      `;
      return;
    }

    this.allRequests.forEach(request => {
      const card = createRequestCard({
        request,
        showStatus: this.config.showStatusColumn ?? true,
        showCreator: this.config.showCreatorColumn ?? true,
        showActions: this.config.isPendingApprovals ?? false,
        isPendingApproval: this.config.isPendingApprovals ?? false,
        enableBulkSelection: this.config.enableBulkSelection ?? false,
        ...(this.config.onRequestClick && { onRequestClick: this.config.onRequestClick }),
        onActionComplete: () => this.loadRequests(),
      });
      this.cardsContainer?.appendChild(card);

      // Setup bulk selection for card if enabled
      if (this.config.enableBulkSelection) {
        const checkbox = card.querySelector('.row-checkbox') as HTMLInputElement;
        if (checkbox) {
          checkbox.checked = bulkSelectionStore.isSelected(request.id);
          checkbox.addEventListener('change', (e) => {
            e.stopPropagation();
            bulkSelectionStore.toggleSelection(request.id);
          });
        }
      }
    });

    // Add infinite scroll sentinel
    if (this.infiniteScroll) {
      const sentinelWrapper = document.createElement('div');
      sentinelWrapper.appendChild(this.infiniteScroll.sentinel);
      this.cardsContainer.appendChild(sentinelWrapper);
    }
  }

  /**
   * Append new cards (for infinite scroll load more)
   */
  private appendCards(newRequests: ExpenditureRequest[]): void {
    if (!this.cardsContainer) return;

    // Remove the sentinel temporarily
    const existingSentinel = this.cardsContainer.querySelector('.infinite-scroll-sentinel');
    existingSentinel?.remove();

    // Append new cards
    newRequests.forEach(request => {
      const card = createRequestCard({
        request,
        showStatus: this.config.showStatusColumn ?? true,
        showCreator: this.config.showCreatorColumn ?? true,
        showActions: this.config.isPendingApprovals ?? false,
        isPendingApproval: this.config.isPendingApprovals ?? false,
        enableBulkSelection: this.config.enableBulkSelection ?? false,
        ...(this.config.onRequestClick && { onRequestClick: this.config.onRequestClick }),
        onActionComplete: () => this.loadRequests(),
      });
      this.cardsContainer?.appendChild(card);

      // Setup bulk selection for card if enabled
      if (this.config.enableBulkSelection) {
        const checkbox = card.querySelector('.row-checkbox') as HTMLInputElement;
        if (checkbox) {
          checkbox.checked = bulkSelectionStore.isSelected(request.id);
          checkbox.addEventListener('change', (e) => {
            e.stopPropagation();
            bulkSelectionStore.toggleSelection(request.id);
          });
        }
      }
    });

    // Re-add the infinite scroll sentinel
    if (this.infiniteScroll) {
      const sentinelWrapper = document.createElement('div');
      sentinelWrapper.appendChild(this.infiniteScroll.sentinel);
      this.cardsContainer.appendChild(sentinelWrapper);
    }
  }

  /**
   * Setup infinite scroll functionality
   */
  private setupInfiniteScroll(): void {
    // Create infinite scroll controller
    this.infiniteScroll = createInfiniteScroll({
      onLoadMore: async () => {
        await this.loadMoreRequests();
      },
      rootMargin: '200px',
      threshold: 0.1,
    });

    // Create scroll-to-top button if enabled
    if (this.config.showScrollToTop) {
      this.scrollToTop = createScrollToTopButton({
        threshold: 400,
        smooth: true,
      });
      document.body.appendChild(this.scrollToTop.button);
    }
  }

  /**
   * Cleanup infinite scroll resources
   */
  private cleanupInfiniteScroll(): void {
    if (this.infiniteScroll) {
      this.infiniteScroll.destroy();
      this.infiniteScroll = null;
    }
    if (this.scrollToTop) {
      this.scrollToTop.destroy();
      this.scrollToTop = null;
    }
  }

  /**
   * Create header section
   */
  private createHeader(): HTMLElement {
    const header = document.createElement('div');
    header.className = 'flex items-center justify-between';

    const titleSection = document.createElement('div');
    titleSection.innerHTML = `
      <h2 class="text-xl font-semibold text-foreground">${escapeHtml(this.config.title)}</h2>
      ${this.config.subtitle ? `<p class="text-sm text-muted-foreground">${escapeHtml(this.config.subtitle)}</p>` : ''}
    `;

    // Actions container
    const actions = document.createElement('div');
    actions.className = 'flex items-center gap-2';

    // Add column picker if enabled
    if (this.config.showColumnPicker && this.config.viewId) {
      this.columnPicker = new ColumnPicker({
        viewId: this.config.viewId,
        columns: this.columnDefinitions,
        onColumnsChange: (columns) => {
          this.visibleColumns = columns;
          this.rebuildTable();
        }
      });
      actions.appendChild(this.columnPicker.getElement());
    }

    // Refresh button
    const refreshBtn = document.createElement('button');
    refreshBtn.type = 'button';
    refreshBtn.className = 'p-2 glass-button rounded-lg hover:bg-accent/10 transition-colors';
    refreshBtn.title = 'Refresh';
    refreshBtn.appendChild(renderIcon('refresh', { size: 'sm' }));
    refreshBtn.addEventListener('click', () => this.loadRequests());
    actions.appendChild(refreshBtn);

    header.appendChild(titleSection);
    header.appendChild(actions);

    return header;
  }

  /**
   * Create filter bar
   */
  private createFilterBar(): HTMLElement {
    const filterBar = document.createElement('div');
    filterBar.className = 'filter-bar glass-sm rounded-lg p-4 space-y-4';

    // Search and filters row
    const topRow = document.createElement('div');
    topRow.className = 'flex flex-wrap gap-3 items-end';

    // Search input
    if (this.config.showSearch) {
      const searchGroup = document.createElement('div');
      searchGroup.className = 'flex-1 min-w-[200px]';
      searchGroup.innerHTML = `
        <label class="block text-xs font-medium text-muted-foreground mb-1.5">Search</label>
        <div class="relative">
          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            ${renderIcon('search', { size: 'sm' }).outerHTML}
          </span>
          <input
            type="text"
            placeholder="Search by title or description..."
            class="w-full pl-9 pr-4 py-2 bg-background/50 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </div>
      `;
      this.searchInput = searchGroup.querySelector('input')!;
      this.searchInput.value = this.filters.search || '';
      this.searchInput.addEventListener('input', debounce(() => {
        if (this.searchInput?.value) {
          this.filters.search = this.searchInput.value;
        } else {
          delete this.filters.search;
        }
        this.page = 1;
        this.loadRequests();
      }, 300));
      topRow.appendChild(searchGroup);
    }

    // Status filter
    if (this.config.showFilters) {
      const statusGroup = document.createElement('div');
      statusGroup.className = 'w-[140px]';
      statusGroup.innerHTML = `
        <label class="block text-xs font-medium text-muted-foreground mb-1.5">Status</label>
        <select class="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary">
          <option value="">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      `;
      const statusSelect = statusGroup.querySelector('select')!;
      statusSelect.value = this.filters.status || '';
      statusSelect.addEventListener('change', () => {
        if (statusSelect.value) {
          this.filters.status = statusSelect.value as RequestStatus;
        } else {
          delete this.filters.status;
        }
        this.page = 1;
        this.loadRequests();
      });
      topRow.appendChild(statusGroup);

      // Currency filter
      const currencyGroup = document.createElement('div');
      currencyGroup.className = 'w-[120px]';
      currencyGroup.innerHTML = `
        <label class="block text-xs font-medium text-muted-foreground mb-1.5">Currency</label>
        <select class="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary">
          <option value="">All</option>
          ${CURRENCIES.map(c => `<option value="${c}">${c}</option>`).join('')}
        </select>
      `;
      const currencySelect = currencyGroup.querySelector('select')!;
      currencySelect.value = this.filters.currency || '';
      currencySelect.addEventListener('change', () => {
        if (currencySelect.value) {
          this.filters.currency = currencySelect.value;
        } else {
          delete this.filters.currency;
        }
        this.page = 1;
        this.loadRequests();
      });
      topRow.appendChild(currencyGroup);

      // Category filter (skip if hideCategoryFilter is true)
      if (!this.config.hideCategoryFilter) {
        const categoryGroup = document.createElement('div');
        categoryGroup.className = 'w-[180px]';
        categoryGroup.innerHTML = `
          <label class="block text-xs font-medium text-muted-foreground mb-1.5">Category</label>
          <select class="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary">
            <option value="">All Categories</option>
            ${CATEGORY_OPTIONS.map(opt => `<option value="${opt.value}">${escapeHtml(opt.label)}</option>`).join('')}
          </select>
        `;
        const categorySelect = categoryGroup.querySelector('select')!;
        categorySelect.value = this.filters.category || '';
        categorySelect.addEventListener('change', () => {
          if (categorySelect.value) {
            this.filters.category = categorySelect.value as ExpenseCategory;
          } else {
            delete this.filters.category;
          }
          this.page = 1;
          this.loadRequests();
        });
        topRow.appendChild(categoryGroup);
      }
    }

    filterBar.appendChild(topRow);

    // Date and amount filters row
    if (this.config.showFilters) {
      const bottomRow = document.createElement('div');
      bottomRow.className = 'flex flex-wrap gap-3 items-end';

      // From date
      const fromDateGroup = document.createElement('div');
      fromDateGroup.className = 'w-[150px]';
      fromDateGroup.innerHTML = `
        <label class="block text-xs font-medium text-muted-foreground mb-1.5">From Date</label>
        <input
          type="date"
          class="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      `;
      const fromDateInput = fromDateGroup.querySelector('input')!;
      fromDateInput.value = this.filters.fromDate || '';
      fromDateInput.addEventListener('change', () => {
        if (fromDateInput.value) {
          this.filters.fromDate = fromDateInput.value;
        } else {
          delete this.filters.fromDate;
        }
        this.page = 1;
        this.loadRequests();
      });
      bottomRow.appendChild(fromDateGroup);

      // To date
      const toDateGroup = document.createElement('div');
      toDateGroup.className = 'w-[150px]';
      toDateGroup.innerHTML = `
        <label class="block text-xs font-medium text-muted-foreground mb-1.5">To Date</label>
        <input
          type="date"
          class="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      `;
      const toDateInput = toDateGroup.querySelector('input')!;
      toDateInput.value = this.filters.toDate || '';
      toDateInput.addEventListener('change', () => {
        if (toDateInput.value) {
          this.filters.toDate = toDateInput.value;
        } else {
          delete this.filters.toDate;
        }
        this.page = 1;
        this.loadRequests();
      });
      bottomRow.appendChild(toDateGroup);

      // Min amount
      const minAmountGroup = document.createElement('div');
      minAmountGroup.className = 'w-[130px]';
      minAmountGroup.innerHTML = `
        <label class="block text-xs font-medium text-muted-foreground mb-1.5">Min Amount</label>
        <input
          type="number"
          placeholder="0"
          min="0"
          step="1"
          class="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      `;
      const minAmountInput = minAmountGroup.querySelector('input')!;
      minAmountInput.value = this.filters.minAmount?.toString() || '';
      minAmountInput.addEventListener('change', () => {
        if (minAmountInput.value) {
          this.filters.minAmount = Number(minAmountInput.value);
        } else {
          delete this.filters.minAmount;
        }
        this.page = 1;
        this.loadRequests();
      });
      bottomRow.appendChild(minAmountGroup);

      // Max amount
      const maxAmountGroup = document.createElement('div');
      maxAmountGroup.className = 'w-[130px]';
      maxAmountGroup.innerHTML = `
        <label class="block text-xs font-medium text-muted-foreground mb-1.5">Max Amount</label>
        <input
          type="number"
          placeholder="∞"
          min="0"
          step="1"
          class="w-full px-3 py-2 bg-background/50 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      `;
      const maxAmountInput = maxAmountGroup.querySelector('input')!;
      maxAmountInput.value = this.filters.maxAmount?.toString() || '';
      maxAmountInput.addEventListener('change', () => {
        if (maxAmountInput.value) {
          this.filters.maxAmount = Number(maxAmountInput.value);
        } else {
          delete this.filters.maxAmount;
        }
        this.page = 1;
        this.loadRequests();
      });
      bottomRow.appendChild(maxAmountGroup);

      // Clear filters button
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors';
      clearBtn.textContent = 'Clear Filters';
      clearBtn.addEventListener('click', () => this.clearFilters());
      bottomRow.appendChild(clearBtn);

      filterBar.appendChild(bottomRow);
    }

    return filterBar;
  }

  /**
   * Create table container
   */
  private createTableContainer(): HTMLElement {
    const tableContainer = document.createElement('div');
    tableContainer.className = 'glass-sm rounded-lg overflow-hidden';

    const tableWrapper = document.createElement('div');
    tableWrapper.className = 'overflow-x-auto';

    this.table = document.createElement('table');
    this.table.className = 'w-full';

    // Create table header based on visible columns
    this.thead = this.createTableHeader();
    const thead = this.thead;

    // Add select-all checkbox event listener
    if (this.config.enableBulkSelection) {
      const selectAllCheckbox = thead.querySelector('.select-all-checkbox') as HTMLInputElement;
      if (selectAllCheckbox) {
        selectAllCheckbox.addEventListener('change', () => {
          if (selectAllCheckbox.checked) {
            bulkSelectionStore.selectAll();
          } else {
            bulkSelectionStore.deselectAll();
          }
        });
      }
    }

    // Table body
    this.tableBody = document.createElement('tbody');
    this.tableBody.className = 'divide-y divide-border';

    this.table.appendChild(this.thead);
    this.table.appendChild(this.tableBody);
    tableWrapper.appendChild(this.table);
    tableContainer.appendChild(tableWrapper);

    return tableContainer;
  }

  /**
   * Create table header based on visible columns
   */
  private createTableHeader(): HTMLElement {
    const thead = document.createElement('thead');
    const tr = document.createElement('tr');
    tr.className = 'border-b border-border bg-muted/30';

    // Add bulk selection checkbox header if enabled
    if (this.config.enableBulkSelection) {
      const checkboxTh = document.createElement('th');
      checkboxTh.className = 'w-10 px-3 py-3';
      checkboxTh.innerHTML = `
        <input
          type="checkbox"
          class="select-all-checkbox w-4 h-4 rounded border-input bg-background"
          title="Select all"
        />
      `;
      tr.appendChild(checkboxTh);
    }

    // Build columns based on visible columns
    this.visibleColumns.forEach(columnId => {
      const columnDef = this.columnDefinitions.find(col => col.id === columnId);
      if (!columnDef) return;

      const th = document.createElement('th');

      // Determine alignment and sorting capability
      const isSortable = ['title', 'amount', 'status', 'category', 'currency', 'creator', 'created_at', 'updated_at', 'submitted_at'].includes(columnId);
      const isRightAlign = columnId === 'amount';
      const isCenterAlign = columnId === 'status' || columnId === 'actions' || columnId === 'approval_progress';

      let alignClass = 'text-left';
      if (isRightAlign) alignClass = 'text-right';
      if (isCenterAlign) alignClass = 'text-center';

      th.className = `${alignClass} px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider`;

      if (isSortable) {
        th.className += ' cursor-pointer hover:text-foreground transition-colors';
        th.setAttribute('data-sort', columnId);

        const span = document.createElement('span');
        span.className = `flex items-center gap-1`;
        if (isRightAlign) span.className += ' justify-end';
        if (isCenterAlign) span.className += ' justify-center';

        span.innerHTML = `${columnDef.label} ${this.renderSortIcon(columnId as SortBy)}`;
        th.appendChild(span);

        // Add sort event listener
        th.addEventListener('click', () => {
          const sortField = columnId as SortBy;
          if (this.sortBy === sortField) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
          } else {
            this.sortBy = sortField;
            this.sortOrder = sortField === 'amount' ? 'desc' : 'asc';
          }
          this.page = 1;
          this.updateTableHeader(thead);
          this.loadRequests();
        });
      } else {
        th.textContent = columnDef.label;
      }

      tr.appendChild(th);
    });

    // Add chevron column for navigation
    const chevronTh = document.createElement('th');
    chevronTh.className = 'w-10';
    tr.appendChild(chevronTh);

    thead.appendChild(tr);
    return thead;
  }

  /**
   * Rebuild the table when columns change
   */
  private rebuildTable(): void {
    if (!this.table || !this.thead) return;

    // Remove old header
    this.thead.remove();

    // Create new header
    this.thead = this.createTableHeader();
    this.table.insertBefore(this.thead, this.tableBody);

    // Re-render the table rows with new columns
    this.renderTableRows();
  }

  /**
   * Render sort icon for column header
   */
  private renderSortIcon(field: SortBy): string {
    if (this.sortBy !== field) {
      return `<span class="text-muted-foreground/50">${renderIcon('chevronDown', { size: 'xs' }).outerHTML}</span>`;
    }
    const icon = this.sortOrder === 'asc' ? 'chevronUp' : 'chevronDown';
    return `<span class="text-primary">${renderIcon(icon, { size: 'xs' }).outerHTML}</span>`;
  }

  /**
   * Update table header sort icons
   */
  private updateTableHeader(thead: HTMLElement): void {
    thead.querySelectorAll('th[data-sort]').forEach(th => {
      const sortField = th.getAttribute('data-sort') as SortBy;
      const span = th.querySelector('span');
      if (span) {
        const text = span.childNodes[0]?.textContent || '';
        span.innerHTML = `${text.trim()} ${this.renderSortIcon(sortField)}`;
      }
    });
  }

  /**
   * Load requests from API (initial load or refresh)
   */
  async loadRequests(): Promise<void> {
    if (this.isLoading) return;
    this.isLoading = true;

    // Reset for fresh load
    this.page = 1;
    this.allRequests = [];

    // Reset infinite scroll state
    if (this.infiniteScroll) {
      this.infiniteScroll.reset();
    }

    this.showLoadingState();

    try {
      const params: Record<string, string | number | boolean | undefined> = {
        page: this.page,
        limit: this.limit,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filters,
      };

      // Clean undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      if (this.config.isPendingApprovals) {
        const response = await apiClient.get<{ data: PendingApproval[] }>(this.config.endpoint, { params });
        this.pendingApprovals = response.data.data || [];
        // Convert to ExpenditureRequest format for display
        this.requests = this.pendingApprovals.map(pa => ({
          id: pa.request.id,
          title: pa.request.title,
          description: null,
          amount: pa.request.amount,
          currency: pa.request.currency,
          status: pa.request.status as RequestStatus,
          created_by: pa.request.createdBy,
          created_at: pa.request.createdAt,
          updated_at: pa.request.createdAt,
          submitted_at: null,
          approver_role: pa.approvalStep.approverRole,
          creator_name: pa.request.creatorName,
        }));
        this.total = this.requests.length;
        this.totalPages = 1;
      } else {
        const response = await apiClient.get<PaginatedResponse<ExpenditureRequest>>(this.config.endpoint, { params });
        this.requests = response.data.data || [];
        this.total = response.data.pagination?.total || 0;
        this.totalPages = response.data.pagination?.totalPages || 0;
      }

      // Update bulk selection store with all request IDs
      if (this.config.enableBulkSelection) {
        bulkSelectionStore.setAllIds(this.requests.map(r => r.id));
      }

      // For infinite scroll, accumulate results
      if (this.config.paginationMode === 'infinite-scroll') {
        this.allRequests = [...this.requests];
        this.renderContentInfinite();

        // Update infinite scroll state
        if (this.infiniteScroll) {
          const hasMore = this.page < this.totalPages;
          this.infiniteScroll.setState({ hasMore });
        }
      } else {
        this.renderContentPaginated();
        this.renderPagination();
      }
    } catch (error) {
      console.error('Failed to load requests:', error);
      this.showErrorState();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Load more requests (for infinite scroll)
   */
  private async loadMoreRequests(): Promise<void> {
    if (this.isLoadingMore || this.page >= this.totalPages) {
      if (this.infiniteScroll) {
        this.infiniteScroll.setState({ hasMore: false });
      }
      return;
    }

    this.isLoadingMore = true;
    this.page++;

    try {
      const params: Record<string, string | number | boolean | undefined> = {
        page: this.page,
        limit: this.limit,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filters,
      };

      // Clean undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await apiClient.get<PaginatedResponse<ExpenditureRequest>>(this.config.endpoint, { params });
      const newRequests = response.data.data || [];

      // Append new requests to accumulated list
      this.allRequests = [...this.allRequests, ...newRequests];
      this.requests = newRequests;

      // Append new rows to table or cards
      if (this.viewMode === 'cards') {
        this.appendCards(newRequests);
      } else {
        this.appendTableRows(newRequests);
      }

      // Update infinite scroll state
      if (this.infiniteScroll) {
        const hasMore = this.page < this.totalPages;
        this.infiniteScroll.setState({ hasMore });
      }
    } catch (error) {
      console.error('Failed to load more requests:', error);
      if (this.infiniteScroll) {
        this.infiniteScroll.setState({
          hasError: true,
          errorMessage: 'Failed to load more requests. Please try again.',
        });
      }
      // Revert page number on error
      this.page--;
      throw error; // Re-throw to let infinite scroll handle the error state
    } finally {
      this.isLoadingMore = false;
    }
  }

  /**
   * Show loading state
   */
  private showLoadingState(): void {
    if (this.viewMode === 'cards') {
      if (!this.cardsContainer) return;
      this.cardsContainer.innerHTML = '';

      for (let i = 0; i < 5; i++) {
        const skeletonCard = document.createElement('div');
        skeletonCard.className = 'glass-sm rounded-lg p-4 mb-3';
        skeletonCard.innerHTML = `
          <div class="flex items-start gap-3 mb-3">
            <div class="flex-1">
              <div class="flex items-start justify-between gap-2 mb-2">
                ${createSkeleton({ width: '70%', height: '1.25rem' }).outerHTML}
                ${createSkeleton({ width: '80px', height: '1.5rem', className: 'rounded-full' }).outerHTML}
              </div>
              <div class="flex items-center gap-3">
                ${createSkeleton({ width: '100px', height: '1rem' }).outerHTML}
                ${createSkeleton({ width: '120px', height: '0.875rem' }).outerHTML}
              </div>
            </div>
          </div>
        `;
        this.cardsContainer.appendChild(skeletonCard);
      }
    } else {
      if (!this.tableBody) return;
      this.tableBody.innerHTML = '';

      for (let i = 0; i < 5; i++) {
        const row = document.createElement('tr');

        // Add checkbox column skeleton if bulk selection enabled
        if (this.config.enableBulkSelection) {
          const checkboxTd = document.createElement('td');
          checkboxTd.className = 'w-10 px-3 py-3';
          checkboxTd.innerHTML = createSkeleton({ width: '16px', height: '16px' }).outerHTML;
          row.appendChild(checkboxTd);
        }

        // Build skeleton cells based on visible columns
        this.visibleColumns.forEach(columnId => {
          const td = document.createElement('td');
          td.className = 'px-4 py-3';

          switch (columnId) {
            case 'title':
              td.innerHTML = `
                ${createSkeleton({ width: '100%', height: '1rem', className: 'mb-1' }).outerHTML}
                ${createSkeleton({ width: '60%', height: '0.75rem' }).outerHTML}
              `;
              break;
            case 'amount':
              td.className += ' text-right';
              td.innerHTML = createSkeleton({ width: '80px', height: '1rem' }).outerHTML;
              break;
            case 'status':
              td.className += ' text-center';
              td.innerHTML = createSkeleton({ width: '70px', height: '1.5rem', className: 'rounded-full mx-auto' }).outerHTML;
              break;
            case 'actions':
              td.className += ' text-center';
              td.innerHTML = createSkeleton({ width: '140px', height: '1.5rem', className: 'mx-auto' }).outerHTML;
              break;
            case 'currency':
            case 'category':
            case 'creator':
            case 'approver_role':
              td.innerHTML = createSkeleton({ width: '100px', height: '1rem' }).outerHTML;
              break;
            case 'description':
              td.innerHTML = `
                ${createSkeleton({ width: '100%', height: '1rem', className: 'mb-1' }).outerHTML}
                ${createSkeleton({ width: '80%', height: '0.75rem' }).outerHTML}
              `;
              break;
            default: // dates
              td.innerHTML = createSkeleton({ width: '80px', height: '1rem' }).outerHTML;
              break;
          }

          row.appendChild(td);
        });

        // Add chevron column skeleton
        const chevronTd = document.createElement('td');
        chevronTd.className = 'px-4 py-3';
        row.appendChild(chevronTd);

        this.tableBody.appendChild(row);
      }
    }
  }

  /**
   * Show error state
   */
  private showErrorState(): void {
    if (this.viewMode === 'cards') {
      if (!this.cardsContainer) return;
      this.cardsContainer.innerHTML = `
        <div class="flex flex-col items-center gap-3 py-12">
          ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
          <p class="text-muted-foreground">Failed to load requests</p>
          <button type="button" class="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
            Try Again
          </button>
        </div>
      `;
      this.cardsContainer.querySelector('button')?.addEventListener('click', () => this.loadRequests());
    } else {
      if (!this.tableBody) return;
      const colSpan = this.visibleColumns.length + 1 + (this.config.enableBulkSelection ? 1 : 0); // +1 for chevron, +1 for checkbox
      this.tableBody.innerHTML = `
        <tr>
          <td colspan="${colSpan}" class="px-4 py-12 text-center">
            <div class="flex flex-col items-center gap-3">
              ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
              <p class="text-muted-foreground">Failed to load requests</p>
              <button type="button" class="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                Try Again
              </button>
            </div>
          </td>
        </tr>
      `;
      this.tableBody.querySelector('button')?.addEventListener('click', () => this.loadRequests());
    }
  }

  /**
   * Render table rows
   */
  private renderTableRows(): void {
    if (!this.tableBody) return;
    this.tableBody.innerHTML = '';

    if (this.requests.length === 0) {
      const colSpan = this.visibleColumns.length + 1 + (this.config.enableBulkSelection ? 1 : 0); // +1 for chevron, +1 for checkbox
      this.tableBody.innerHTML = `
        <tr>
          <td colspan="${colSpan}" class="px-4 py-12 text-center">
            <div class="flex flex-col items-center gap-3">
              ${renderIcon('fileText', { size: 'xl', className: 'text-muted-foreground/50' }).outerHTML}
              <p class="text-muted-foreground">${escapeHtml(this.config.emptyMessage || 'No requests found')}</p>
            </div>
          </td>
        </tr>
      `;
      return;
    }

    this.requests.forEach(request => {
      const row = this.createTableRow(request);
      this.tableBody?.appendChild(row);
    });
  }

  /**
   * Render table rows for infinite scroll mode
   * Includes the sentinel element at the end
   */
  private renderTableRowsInfinite(): void {
    if (!this.tableBody) return;
    this.tableBody.innerHTML = '';

    if (this.allRequests.length === 0) {
      const colSpan = this.visibleColumns.length + 1 + (this.config.enableBulkSelection ? 1 : 0); // +1 for chevron, +1 for checkbox
      this.tableBody.innerHTML = `
        <tr>
          <td colspan="${colSpan}" class="px-4 py-12 text-center">
            <div class="flex flex-col items-center gap-3">
              ${renderIcon('fileText', { size: 'xl', className: 'text-muted-foreground/50' }).outerHTML}
              <p class="text-muted-foreground">${escapeHtml(this.config.emptyMessage || 'No requests found')}</p>
            </div>
          </td>
        </tr>
      `;
      return;
    }

    // Render all accumulated requests
    this.allRequests.forEach(request => {
      const row = this.createTableRow(request);
      this.tableBody?.appendChild(row);
    });

    // Add the infinite scroll sentinel
    this.addInfiniteScrollSentinel();
  }

  /**
   * Append new rows to the table (for infinite scroll load more)
   */
  private appendTableRows(newRequests: ExpenditureRequest[]): void {
    if (!this.tableBody) return;

    // Remove the sentinel temporarily
    const existingSentinel = this.tableBody.querySelector('.infinite-scroll-sentinel-row');
    existingSentinel?.remove();

    // Append new rows
    newRequests.forEach(request => {
      const row = this.createTableRow(request);
      this.tableBody?.appendChild(row);
    });

    // Re-add the infinite scroll sentinel
    this.addInfiniteScrollSentinel();
  }

  /**
   * Add the infinite scroll sentinel to the table body
   */
  private addInfiniteScrollSentinel(): void {
    if (!this.tableBody || !this.infiniteScroll) return;

    const colSpan = this.visibleColumns.length + 1 + (this.config.enableBulkSelection ? 1 : 0); // +1 for chevron, +1 for checkbox
    const sentinelRow = document.createElement('tr');
    sentinelRow.className = 'infinite-scroll-sentinel-row';
    const sentinelCell = document.createElement('td');
    sentinelCell.colSpan = colSpan;
    sentinelCell.appendChild(this.infiniteScroll.sentinel);
    sentinelRow.appendChild(sentinelCell);
    this.tableBody.appendChild(sentinelRow);
  }

  /**
   * Create a table row for a request
   */
  private createTableRow(request: ExpenditureRequest): HTMLElement {
    const row = document.createElement('tr');
    row.className = 'hover:bg-muted/20 transition-colors cursor-pointer';
    row.setAttribute('data-request-id', request.id);

    const statusConfig = STATUS_CONFIG[request.status];

    // Add bulk selection checkbox cell if enabled
    if (this.config.enableBulkSelection) {
      const checkboxTd = document.createElement('td');
      checkboxTd.className = 'px-3 py-3';
      checkboxTd.setAttribute('onclick', 'event.stopPropagation()');
      checkboxTd.innerHTML = `<input type="checkbox" class="row-checkbox w-4 h-4 rounded border-input bg-background" data-id="${request.id}" />`;
      row.appendChild(checkboxTd);
    }

    // Build cells based on visible columns
    this.visibleColumns.forEach(columnId => {
      const td = document.createElement('td');
      td.className = 'px-4 py-3';

      switch (columnId) {
        case 'title':
          td.innerHTML = `
            <div class="font-medium text-foreground line-clamp-1">${escapeHtml(request.title) || 'Untitled'}</div>
            ${request.approver_role && this.config.isPendingApprovals ? `<div class="text-xs text-muted-foreground">Approving as ${request.approver_role}</div>` : ''}
          `;
          break;

        case 'amount':
          td.className += ' text-right';
          td.innerHTML = `<span class="font-semibold text-foreground">${formatAmount(request.amount, request.currency)}</span>`;
          break;

        case 'currency':
          td.className += ' text-sm text-muted-foreground';
          td.textContent = request.currency || '-';
          break;

        case 'category':
          td.className += ' text-sm text-muted-foreground';
          td.textContent = request.category || '-';
          break;

        case 'description':
          td.className += ' text-sm text-muted-foreground';
          const description = request.description || '-';
          td.innerHTML = `<span class="line-clamp-2">${escapeHtml(description)}</span>`;
          break;

        case 'status':
          td.className += ' text-center';
          td.innerHTML = `
            <span class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${statusConfig.bgClass} ${statusConfig.colorClass}">
              ${renderIcon(statusConfig.icon, { size: 'xs' }).outerHTML}
              ${statusConfig.label}
            </span>
          `;
          break;

        case 'creator':
          td.className += ' text-sm text-muted-foreground';
          td.textContent = request.creator_name || '-';
          break;

        case 'created_at':
          td.className += ' text-sm text-muted-foreground';
          td.textContent = formatDate(request.created_at);
          break;

        case 'updated_at':
          td.className += ' text-sm text-muted-foreground';
          td.textContent = formatDate(request.updated_at);
          break;

        case 'submitted_at':
          td.className += ' text-sm text-muted-foreground';
          td.textContent = formatDate(request.submitted_at);
          break;

        case 'approval_progress':
          td.className += ' text-center';
          if (request.status === 'DRAFT' || !request.approvalProgress) {
            td.textContent = '-';
          } else {
            const dots = request.approvalProgress.steps.map((step) => {
              let dotClass = 'w-2 h-2 rounded-full inline-block';
              let dotColor = '';

              switch (step.status) {
                case 'APPROVED':
                  dotColor = 'bg-success';
                  break;
                case 'REJECTED':
                  dotColor = 'bg-destructive';
                  break;
                case 'PENDING':
                default:
                  dotColor = 'bg-muted-foreground';
                  break;
              }

              return `<span class="${dotClass} ${dotColor}"></span>`;
            }).join('<span class="mx-0.5"></span>');

            td.innerHTML = `<div class="flex items-center justify-center">${dots}</div>`;
          }
          break;

        case 'approver_role':
          td.className += ' text-sm text-muted-foreground';
          td.textContent = request.approver_role || '-';
          break;

        case 'actions':
          if (this.config.isPendingApprovals) {
            td.className += ' text-center';
            const actionButtons = createInlineApprovalActions({
              request: {
                id: request.id,
                title: request.title,
                amount: request.amount,
                currency: request.currency,
              },
              onActionComplete: () => {
                // Refresh the list after successful action
                this.loadRequests();
              },
            });
            td.appendChild(actionButtons);
          }
          break;
      }

      row.appendChild(td);
    });

    // Add chevron column
    const chevronCell = document.createElement('td');
    chevronCell.className = 'px-4 py-3';
    chevronCell.innerHTML = `
      <span class="text-muted-foreground hover:text-foreground transition-colors">
        ${renderIcon('chevronRight', { size: 'sm' }).outerHTML}
      </span>
    `;
    row.appendChild(chevronCell);

    // Handle checkbox click
    if (this.config.enableBulkSelection) {
      const checkbox = row.querySelector('.row-checkbox') as HTMLInputElement;
      if (checkbox) {
        // Set initial state from store
        checkbox.checked = bulkSelectionStore.isSelected(request.id);

        checkbox.addEventListener('change', (e) => {
          e.stopPropagation();
          bulkSelectionStore.toggleSelection(request.id);
        });
      }
    }

    // Handle row click (but not on checkbox)
    row.addEventListener('click', (e) => {
      // Don't navigate if clicking on checkbox or its cell
      if ((e.target as HTMLElement).closest('td')?.querySelector('.row-checkbox')) {
        return;
      }

      if (this.config.onRequestClick) {
        this.config.onRequestClick(request);
      } else {
        // Default: navigate to request detail
        window.history.pushState({}, '', `/requests/${request.id}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    });

    return row;
  }

  /**
   * Render pagination controls
   */
  private renderPagination(): void {
    if (!this.paginationContainer) return;

    this.paginationContainer.innerHTML = `
      <div class="text-sm text-muted-foreground">
        Showing ${this.requests.length > 0 ? (this.page - 1) * this.limit + 1 : 0} - ${Math.min(this.page * this.limit, this.total)} of ${this.total}
      </div>
      <div class="flex items-center gap-2">
        <button type="button" class="p-2 glass-button rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" ${this.page <= 1 ? 'disabled' : ''} data-action="prev">
          ${renderIcon('chevronLeft', { size: 'sm' }).outerHTML}
        </button>
        <span class="text-sm text-muted-foreground px-3">
          Page ${this.page} of ${this.totalPages || 1}
        </span>
        <button type="button" class="p-2 glass-button rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" ${this.page >= this.totalPages ? 'disabled' : ''} data-action="next">
          ${renderIcon('chevronRight', { size: 'sm' }).outerHTML}
        </button>
      </div>
    `;

    // Add pagination event listeners
    this.paginationContainer.querySelector('[data-action="prev"]')?.addEventListener('click', () => {
      if (this.page > 1) {
        this.page--;
        this.loadRequests();
      }
    });

    this.paginationContainer.querySelector('[data-action="next"]')?.addEventListener('click', () => {
      if (this.page < this.totalPages) {
        this.page++;
        this.loadRequests();
      }
    });
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    // Preserve the 'mine' filter from initialFilters if it was set
    const preservedMine = this.config.initialFilters?.mine;
    this.filters = preservedMine !== undefined ? { mine: preservedMine } : {};
    this.page = 1;
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    // Re-render filter bar to reset all inputs
    this.render();
    this.loadRequests();
  }

  /**
   * Set filters programmatically
   */
  setFilters(filters: RequestListFilters): void {
    this.filters = { ...filters };
    this.page = 1;
    this.loadRequests();
  }

  /**
   * Refresh the list
   */
  refresh(): void {
    this.loadRequests();
  }

  /**
   * Get the container element
   */
  getElement(): HTMLElement {
    return this.container;
  }

  /**
   * Setup bulk actions toolbar and selection listeners
   */
  private setupBulkActions(): void {
    // Create and mount bulk action toolbar
    const bulkConfig: BulkActionConfig = {
      showApprove: !!this.config.onBulkApprove,
      showReject: !!this.config.onBulkReject,
      showExport: !!this.config.onBulkExport,
    };
    if (this.config.onBulkApprove) bulkConfig.onApprove = this.config.onBulkApprove;
    if (this.config.onBulkReject) bulkConfig.onReject = this.config.onBulkReject;
    if (this.config.onBulkExport) bulkConfig.onExport = this.config.onBulkExport;
    this.bulkActionToolbar = new BulkActionToolbar(bulkConfig);
    this.bulkActionToolbar.mount(document.body);

    // Subscribe to selection changes to update UI
    this.selectionUnsubscribe = bulkSelectionStore.subscribe((selectedIds) => {
      this.updateSelectionUI(selectedIds);
    });
  }

  /**
   * Update UI based on selection state
   */
  private updateSelectionUI(selectedIds: Set<string>): void {
    // Update select-all checkbox state
    const selectAllCheckbox = this.container.querySelector('.select-all-checkbox') as HTMLInputElement;
    if (selectAllCheckbox) {
      if (selectedIds.size === 0) {
        selectAllCheckbox.checked = false;
        selectAllCheckbox.indeterminate = false;
      } else if (bulkSelectionStore.isAllSelected()) {
        selectAllCheckbox.checked = true;
        selectAllCheckbox.indeterminate = false;
      } else {
        selectAllCheckbox.checked = false;
        selectAllCheckbox.indeterminate = true;
      }
    }

    // Update row checkboxes
    this.tableBody?.querySelectorAll('.row-checkbox').forEach(checkbox => {
      const input = checkbox as HTMLInputElement;
      const id = input.getAttribute('data-id');
      if (id) {
        input.checked = selectedIds.has(id);
      }
    });
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    this.cleanupInfiniteScroll();
    if (this.bulkActionToolbar) {
      this.bulkActionToolbar.destroy();
    }
    if (this.selectionUnsubscribe) {
      this.selectionUnsubscribe();
    }
    if (this.screenSizeUnsubscribe) {
      this.screenSizeUnsubscribe();
    }
    bulkSelectionStore.reset();
    this.container.remove();
  }

  /**
   * Get the total count of loaded items (useful for infinite scroll)
   */
  getLoadedCount(): number {
    return this.config.paginationMode === 'infinite-scroll'
      ? this.allRequests.length
      : this.requests.length;
  }

  /**
   * Get the total count of all available items
   */
  getTotalCount(): number {
    return this.total;
  }

  /**
   * Check if there are more items to load
   */
  hasMoreItems(): boolean {
    return this.page < this.totalPages;
  }
}

/**
 * Create a request list component (factory function)
 */
export function createRequestList(config: RequestListConfig): RequestList {
  return new RequestList(config);
}
